import { supabase as _supabase } from './supabase';

const db = _supabase as any;

export type ActivityType = 'progress' | 'respondents' | 'reports' | 'programs';

export interface UnseenCounts {
  progress: number;
  respondents: number;
  reports: number;
  programs: number;
}

export async function fetchUnseenCounts(coachUserId: string): Promise<UnseenCounts> {
  const { data: coach } = await db
    .from('coaches')
    .select('id')
    .eq('user_id', coachUserId)
    .maybeSingle();

  if (!coach) return { progress: 0, respondents: 0, reports: 0, programs: 0 };

  const { data: respondents } = await db
    .from('respondents')
    .select('user_id')
    .eq('coach_id', coach.id);

  if (!respondents || respondents.length === 0) {
    return { progress: 0, respondents: 0, reports: 0, programs: 0 };
  }

  const userIds = respondents.map((r: { user_id: string }) => r.user_id);

  const { data: seenRows } = await db
    .from('coach_activity_seen')
    .select('activity_type, last_seen_at')
    .eq('coach_id', coachUserId);

  const seenMap = new Map<string, string>();
  (seenRows || []).forEach((r: { activity_type: string; last_seen_at: string }) => {
    seenMap.set(r.activity_type, r.last_seen_at);
  });

  const progressSince = seenMap.get('progress') || '1970-01-01T00:00:00Z';
  const respondentsSince = seenMap.get('respondents') || '1970-01-01T00:00:00Z';
  const reportsSince = seenMap.get('reports') || '1970-01-01T00:00:00Z';
  const programsSince = seenMap.get('programs') || '1970-01-01T00:00:00Z';

  const [goals, tasks, checkIns, journals, milestones, habitCompletions, results, newRespondents, completedPrograms] = await Promise.all([
    db.from('goals').select('id', { count: 'exact', head: true }).in('user_id', userIds).gte('created_at', progressSince),
    db.from('tasks').select('id', { count: 'exact', head: true }).in('user_id', userIds).gte('created_at', progressSince),
    db.from('check_ins').select('id', { count: 'exact', head: true }).in('user_id', userIds).gte('checked_in_at', progressSince),
    db.from('journal_entries').select('id', { count: 'exact', head: true }).in('user_id', userIds).gte('created_at', progressSince),
    db.from('milestones').select('id', { count: 'exact', head: true }).in('user_id', userIds).gte('achieved_at', progressSince),
    db.from('habit_completions').select('id', { count: 'exact', head: true }).in('user_id', userIds).gte('created_at', progressSince),
    db.from('results').select('id', { count: 'exact', head: true }).in('user_id', userIds).gte('created_at', respondentsSince),
    db.from('respondents').select('id', { count: 'exact', head: true }).eq('coach_id', coach.id).gte('created_at', respondentsSince),
    db.from('program_assignments').select('id', { count: 'exact', head: true }).in('assignee_user_id', userIds).not('completed_at', 'is', null).gte('completed_at', programsSince),
  ]);

  const progressCount =
    (goals.count || 0) + (tasks.count || 0) + (checkIns.count || 0) +
    (journals.count || 0) + (milestones.count || 0) + (habitCompletions.count || 0);

  const respondentsCount = (results.count || 0) + (newRespondents.count || 0);

  const { count: reportsCount } = await db
    .from('results')
    .select('id', { count: 'exact', head: true })
    .in('user_id', userIds)
    .gte('created_at', reportsSince);

  return {
    progress: progressCount,
    respondents: respondentsCount,
    reports: reportsCount || 0,
    programs: completedPrograms.count || 0,
  };
}

export async function markActivitySeen(coachUserId: string, activityType: ActivityType): Promise<void> {
  const now = new Date().toISOString();
  const { data: existing } = await db
    .from('coach_activity_seen')
    .select('id')
    .eq('coach_id', coachUserId)
    .eq('activity_type', activityType)
    .maybeSingle();

  if (existing) {
    await db
      .from('coach_activity_seen')
      .update({ last_seen_at: now, updated_at: now })
      .eq('id', existing.id);
  } else {
    await db
      .from('coach_activity_seen')
      .insert({ coach_id: coachUserId, activity_type: activityType, last_seen_at: now });
  }
}
